/**
 * One producer on the world screen (D245, D438, D439): its tile, name with the owned count and
 * the hand, its rate, its bar with the next milestone, a state line only when it has something
 * to say, then its three buys. A locked producer is the next goal, with its unlock cost. Words
 * are in the interface font and figures in the number font (D261); every control is at least
 * 44px.
 */
import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { now } from '../clock.ts';
import {
  Bounce,
  fillAt,
  flyCoins,
  playSound,
  popFromCorner,
  useReducedMotion,
} from '../juice/index.ts';
import { Icon } from '../ui/Icon.tsx';
import { offProps, UNSTRUCK } from '../ui/OffNote.tsx';
import './idle.css';
import { coins, gain } from './notation.ts';
import { cap, t as term } from './terms.ts';
import { BtnLabel, BuyButton, Figs } from './BuyButton.tsx';

export { Figs };
import {
  barAt,
  isFastBar,
  aTerm,
  payLabel,
  rateText,
  type ChainRow,
  type LockedRow,
  type BuyMode,
  type ProducerRow,
} from './view.ts';

/** A bar re-draws at least this far apart, however short it is. */
const MIN_REDRAW_MS = 200;
/** Hand-run payouts pop and fly at most this often per row, so a fast bar is not a blizzard. */
const PAYOUT_JUICE_MS = 1500;
/** And the coin sound for them at most this often across the world. */
const PAYOUT_SOUND_MS = 2500;
let lastPayoutSound = 0;

/** Draw again when the bar next ends, so it restarts or turns ready on time. */
function useBarClock(nextAt: number | null): number {
  const [, force] = useState(0);
  useEffect(() => {
    if (nextAt === null) return;
    const id = setTimeout(() => force((n) => n + 1), Math.max(MIN_REDRAW_MS, nextAt - now()));
    return () => clearTimeout(id);
  });
  return now();
}

const ROW: JSX.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '64px minmax(0, 1fr) auto',
  gridTemplateRows: 'auto auto',
  columnGap: 10,
  rowGap: 6,
  alignItems: 'center',
  padding: 8,
  borderRadius: 'var(--radius-md)',
  background: 'var(--surface)',
  border: '1px solid var(--line)',
};

const SMALL: JSX.CSSProperties = {
  font: '400 var(--card-body)/1.3 var(--font-ui)',
  color: 'var(--ink-2)',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

/** D439: the icon tile, 40 square, 8px radius, `surface-2`. */
const TILE: JSX.CSSProperties = {
  flex: 'none',
  width: 40,
  height: 40,
  borderRadius: 'var(--radius-md)',
  background: 'var(--surface-2)',
  color: 'var(--ink)',
  display: 'grid',
  placeItems: 'center',
  boxSizing: 'border-box',
};

/**
 * D439: the card, 12px radius, 10px padding. Its height is its content's and never an explicit
 * minimum (a grid that must scroll squeezes a row to its minimum, D438).
 */
const CARD: JSX.CSSProperties = {
  boxSizing: 'border-box',
  borderRadius: 'var(--radius-lg)',
  background: 'var(--surface)',
  border: '1px solid var(--line)',
  padding: 10,
  display: 'grid',
  gap: 8,
};

/** The Buy and Hire buttons' width: fixed, whatever the price (item 273). */
const BUY_W = 96;

type Look = 'primary' | 'secondary' | 'accent';

/**
 * D439: the one button of the producer card and the chain card, 44px, 8px radius, two lines:
 * the action (13px 600) over its price or its shortfall (11px 500 mono). Primary is the accent
 * fill, secondary a ring in `ink-2`, accent a ring and text in the accent. A disabled one takes
 * base.css's off look and says what it needs on its second line, unstruck.
 */
function TwoLine({
  look,
  action,
  line,
  off,
  reason,
  label,
  testId,
  width,
  tall,
  onClick,
}: {
  look: Look;
  action: string;
  line: string;
  off: boolean;
  /** What a press on a disabled one says. */
  reason?: string | undefined;
  label: string;
  testId?: string;
  width?: number;
  /** The producer row's Buy: 52 tall (D454). */
  tall?: boolean;
  onClick: (e: MouseEvent) => void;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      {...offProps(off, reason)}
      aria-label={label}
      onClick={off ? undefined : (e) => onClick(e)}
      style={{
        boxSizing: 'border-box',
        width: width ?? '100%',
        flex: 'none',
        minWidth: 0,
        height: 44,
        ...(tall ? { height: 52 } : {}),
        padding: '0 4px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        overflow: 'hidden',
        border: 0,
        borderRadius: 'var(--radius-md)',
        cursor: 'pointer',
        touchAction: 'manipulation',
        ...(look === 'primary'
          ? { background: 'var(--accent)', color: 'var(--accent-ink)' }
          : look === 'accent'
            ? {
                background: 'var(--surface)',
                color: 'var(--accent)',
                boxShadow: 'inset 0 0 0 1.5px var(--accent)',
              }
            : {
                background: 'var(--surface)',
                color: 'var(--ink)',
                // ink-2, not line: the ring reaches 3:1 on the card.
                boxShadow: 'inset 0 0 0 1.5px var(--ink-2)',
              }),
      }}
    >
      <span style={{ font: '600 13px/1 var(--font-ui)', whiteSpace: 'nowrap' }}>
        <Figs text={action} />
      </span>
      <span
        class="num"
        style={{ font: `500 ${off ? 10 : 11}px/1 var(--font-num)`, whiteSpace: 'nowrap' }}
      >
        {off ? (
          // The button's line-through reaches a flex item, but not an inline block inside it.
          <span style={UNSTRUCK}>
            <Figs text={line} />
          </span>
        ) : (
          line
        )}
      </span>
    </button>
  );
}

/** The price on a two-line button: the verb above, the cost under it in the number font. */
function PriceLabel({ verb, cost }: { verb: string; cost: number }) {
  return <BtnLabel action={<Figs text={verb} />} price={coins(cost)} />;
}

export interface ProducerRowProps {
  row: ProducerRow;
  /** The Producers header's switch: which of the row's offers its one Buy button makes. */
  mode: BuyMode;
  onTap: (row: ProducerRow, el: Element) => void;
  onBuy: (row: ProducerRow, n: number, el: Element) => void;
  onHire: (row: ProducerRow, el: Element) => void;
}

/** The harvest circle: 60 across, a 5px ring inside it, a 42px disc inside that. */
const CIRCLE = 60;
const RING_W = 5;
const RING_R = (CIRCLE - RING_W) / 2;
const RING_C = 2 * Math.PI * RING_R;
const DISC = 42;
const BADGE = 22;

/** A pointing hand, for the tap badge. */
function TapGlyph({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <path d="M22 14a8 8 0 0 1-8 8" />
      <path d="M18 11v-1a2 2 0 0 0-2-2a2 2 0 0 0-2 2" />
      <path d="M14 10V9a2 2 0 0 0-2-2a2 2 0 0 0-2 2v1" />
      <path d="M10 9.5V4a2 2 0 0 0-2-2a2 2 0 0 0-2 2v10" />
      <path d="M18 11a2 2 0 1 1 4 0v3a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
    </svg>
  );
}

/** D459: a person, for the hired hand's badge and the Hire line; the same glyph in both. */
function HandGlyph({ size }: { size: number }) {
  return <Icon name="user" size={size} />;
}

/**
 * The ring round the disc: a track, and over it the bar. Running it is one CSS animation keyed
 * to the cycle's start (item 226's way, as `ProgressBar`); still, it is the fill at `now`.
 * Not started it is the empty track; a bar too fast to follow is full.
 */
function Ring({
  start,
  duration,
  now: at,
  running,
  full,
  color,
}: {
  start: number;
  duration: number;
  now: number;
  running: boolean;
  full: boolean;
  color: string;
}) {
  const reduced = useReducedMotion();
  const key = `${start}:${duration}`;
  const drawn = useRef<{ key: string; at: number } | null>(null);
  if (drawn.current?.key !== key) drawn.current = { key, at };
  const done = full ? 1 : running ? fillAt(start, duration, at) : 0;
  const moving = running && !full && !reduced && done < 1 && duration > 0;
  return (
    <svg
      class="idle-ring"
      width={CIRCLE}
      height={CIRCLE}
      viewBox={`0 0 ${CIRCLE} ${CIRCLE}`}
      aria-hidden="true"
      style={{ position: 'absolute', inset: 0, transform: 'rotate(-90deg)' }}
    >
      <circle
        cx={CIRCLE / 2}
        cy={CIRCLE / 2}
        r={RING_R}
        fill="none"
        stroke="var(--surface-2)"
        stroke-width={RING_W}
      />
      <circle
        key={key}
        cx={CIRCLE / 2}
        cy={CIRCLE / 2}
        r={RING_R}
        fill="none"
        stroke={color}
        stroke-width={RING_W}
        stroke-dasharray={RING_C}
        stroke-dashoffset={moving ? RING_C : RING_C * (1 - done)}
        style={
          moving
            ? {
                animation: `idle-ring ${Math.round(duration)}ms linear ${-Math.round(drawn.current.at - start)}ms forwards`,
              }
            : undefined
        }
      />
    </svg>
  );
}

export function ProducerRowView({ row, mode, onTap, onBuy, onHire }: ProducerRowProps) {
  const fast = isFastBar(row);
  const bar = barAt(row, now());
  // A fast bar is steady, so the row only wakes to pay out its coins now and then.
  const t = useBarClock(fast ? now() + PAYOUT_JUICE_MS : bar.nextAt);
  const { start, ready } = barAt(row, t);
  const circleRef = useRef<HTMLDivElement>(null);
  // A hand-run bar pays as it ends: the coins pop off it and fly to the counter.
  const lastJuice = useRef(0);
  const lastStart = useRef(start);
  useEffect(() => {
    if (!row.hand || start === lastStart.current) return;
    lastStart.current = start;
    if (t - lastJuice.current < PAYOUT_JUICE_MS || !circleRef.current) return;
    lastJuice.current = t;
    popFromCorner(circleRef.current, gain(fast ? (row.rate * PAYOUT_JUICE_MS) / 1000 : row.payout));
    flyCoins(circleRef.current, { count: 2 });
    if (t - lastPayoutSound >= PAYOUT_SOUND_MS) {
      lastPayoutSound = t;
      playSound('coin');
    }
  }, [start, row.hand]);

  const canTap = !row.hand && (row.idle || ready);
  // D454: ready is something waiting (a full bar); a bar not started or still growing, with no
  // hand, is the tap-me state.
  const waiting = !row.hand && ready;
  const tapState = canTap ? 'ready' : row.tapSpent ? 'spent' : row.hand ? 'hand' : 'growing';
  const tapLabel = waiting
    ? `Collect ${row.name.toLowerCase()}`
    : canTap
      ? `Tap ${row.name.toLowerCase()}`
      : row.tapSpent
        ? `${row.name}, ${aTerm(term('hand'))} runs it. Taps have sped this harvest all they can`
        : `${row.name}, ${row.hand ? `${aTerm(term('hand'))} runs it` : 'growing'}. Tap to speed it up`;
  // The count's progress to the next milestone, as the track's bar.
  const trackPct =
    row.milestoneAt === null || row.milestoneAt <= 0
      ? 100
      : Math.max(0, Math.min(100, (row.owned / row.milestoneAt) * 100));
  const offer = row.buys[mode];
  const short = offer.needs !== undefined ? coins(offer.needs) : null;
  const what = `Buy ${offer.n} ${row.name.toLowerCase()}`;
  const ringColor = waiting ? 'var(--coin)' : 'var(--accent)';
  const noHand = !row.hand && !waiting;
  return (
    <div
      style={{
        ...CARD,
        // D439: ready is a 1px accent border and a 1px accent inset ring; the fill stays.
        ...(waiting
          ? { borderColor: 'var(--accent)', boxShadow: 'inset 0 0 0 1px var(--accent)' }
          : {}),
      }}
      data-testid={`producer-${row.id}`}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `${CIRCLE}px minmax(0, 1fr) ${BUY_W}px`,
          columnGap: 10,
          alignItems: 'center',
        }}
      >
        {/* D454: the harvest circle is the row's only tap. D261: a growing or hand-run row
            reads as a status, never as a control turned off; D277: a tap on it still speeds
            the cycle under way. */}
        <div ref={circleRef} style={{ position: 'relative', width: CIRCLE, height: CIRCLE }}>
          <button
            type="button"
            data-testid={`tap-${row.id}`}
            data-state={tapState}
            aria-label={tapLabel}
            class="idle-tap"
            onClick={(e) => onTap(row, e.currentTarget)}
            style={{
              position: 'absolute',
              inset: 0,
              padding: 0,
              border: 0,
              borderRadius: '50%',
              background: 'transparent',
              cursor: 'pointer',
              touchAction: 'manipulation',
            }}
          >
            <Ring
              start={start}
              duration={row.cycleMs}
              now={t}
              running={row.running}
              full={fast}
              color={ringColor}
            />
            <span
              data-disc=""
              style={{
                position: 'absolute',
                left: (CIRCLE - DISC) / 2,
                top: (CIRCLE - DISC) / 2,
                width: DISC,
                height: DISC,
                boxSizing: 'border-box',
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                // D277: a bar taps have sped all they may is quiet until the next one.
                opacity: row.tapSpent ? 0.7 : 1,
                ...(waiting
                  ? { background: 'var(--coin)', color: 'var(--on-coin)' }
                  : noHand
                    ? {
                        background: 'color-mix(in srgb, var(--accent) 16%, var(--surface-2))',
                        boxShadow: 'inset 0 0 0 2px var(--accent)',
                        color: 'var(--ink)',
                      }
                    : { background: 'var(--surface-2)', color: 'var(--ink)' }),
              }}
            >
              <Bounce trigger={row.owned}>
                <Icon name={row.icon} size={24} />
              </Bounce>
            </span>
          </button>
          {/* D459: a person in the ink circle means run by a hand; the tap glyph in the accent
              circle only while the row can be tapped. */}
          {(row.hand || !row.tapSpent) && (
            <span
              aria-hidden="true"
              data-badge={row.hand ? 'hand' : 'tap'}
              style={{
                position: 'absolute',
                right: -2,
                bottom: -2,
                width: BADGE,
                height: BADGE,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                pointerEvents: 'none',
                border: '2px solid var(--surface)',
                boxSizing: 'content-box',
                ...(row.hand
                  ? { background: 'var(--ink)', color: 'var(--surface)' }
                  : { background: 'var(--accent)', color: 'var(--accent-ink)' }),
              }}
            >
              {row.hand ? <HandGlyph size={12} /> : <TapGlyph size={12} />}
            </span>
          )}
        </div>
        <div style={{ minWidth: 0, display: 'grid', gap: 4 }}>
          {/* The name and the rate; a fixed height, so the line never moves as the rate ticks
              over (D320, D328). */}
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              columnGap: 6,
              minWidth: 0,
              height: 20,
            }}
          >
            <span
              title={row.name}
              style={{
                font: '600 15px/1.3 var(--font-ui)',
                minWidth: 0,
                flex: '0 1 auto',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {row.name}
            </span>
            <span
              class="num"
              data-testid={`rate-${row.id}`}
              style={{
                flex: 'none',
                whiteSpace: 'nowrap',
                font: '700 13px/1.4 var(--font-num)',
                color: waiting ? 'var(--coin-ink)' : 'var(--ink)',
              }}
            >
              {payLabel(row)}
            </span>
          </div>
          {/* What this row wants of the player. */}
          <div
            data-testid={waiting ? `ready-${row.id}` : undefined}
            style={{
              font: `${waiting || noHand ? 600 : 400} 11px/14px var(--font-ui)`,
              color: waiting ? 'var(--coin-ink)' : noHand ? 'var(--accent)' : 'var(--ink-2)',
            }}
          >
            {waiting
              ? 'Tap to collect'
              : noHand
                ? `${cap(term('tap'))} to ${term('harvest')}`
                : `Run by ${aTerm(term('hand'))}`}
          </div>
          {/* The milestone track: the count, where the next one is, and how far along. */}
          <div style={{ display: 'grid', gap: 3, minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 6,
              }}
            >
              <span
                data-testid={`owned-${row.id}`}
                role="img"
                aria-label={`${row.owned} owned`}
                class="num"
                style={{ font: '500 11px/1.2 var(--font-num)', color: 'var(--ink)' }}
              >
                {row.owned}
              </span>
              {row.milestoneAt !== null && (
                <span
                  data-milestone-tick=""
                  class="tnum"
                  title={row.milestone ?? undefined}
                  style={{
                    font: '500 var(--fs-badge)/1.2 var(--font-ui)',
                    color: 'var(--ink-2)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  next at {row.milestoneAt}
                </span>
              )}
            </div>
            <div
              data-milestone-bar=""
              role="progressbar"
              aria-label={`${row.name}${row.milestone ? `, ${row.milestone}` : ''}`}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(trackPct)}
              style={{
                height: 4,
                borderRadius: 2,
                overflow: 'hidden',
                background: 'var(--surface-2)',
              }}
            >
              <span
                style={{
                  display: 'block',
                  height: '100%',
                  width: `${trackPct}%`,
                  background: 'var(--ink-2)',
                }}
              />
            </div>
          </div>
          {/* D271: a building on this producer, as a chip under the track; never the name
              line (item 270). */}
          {(row.badges.length > 0 || row.links.length > 0) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, minWidth: 0 }}>
              {row.badges.map((b) => (
                <span
                  key={b}
                  data-testid={`badge-${row.id}`}
                  class="idle-chip"
                  style={{
                    flex: 'none',
                    padding: '0 6px',
                    borderRadius: 'var(--radius-sm)',
                    background: 'color-mix(in srgb, var(--coin) 28%, var(--surface))',
                    color: 'var(--coin-ink)',
                    height: 'var(--chip-h)',
                    boxSizing: 'border-box',
                    font: '600 var(--fs-note)/18px var(--font-ui)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Figs text={b} />
                </span>
              ))}
              {/* D454 step 3: what neighbours do, after the chain chip. */}
              {row.links.map((l) => (
                <span
                  key={l.text}
                  data-testid={`link-${row.id}`}
                  data-role={l.role}
                  class="idle-chip"
                  style={{
                    flex: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 3,
                    padding: '0 6px',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--surface)',
                    border: '1px solid var(--accent)',
                    color: 'var(--accent)',
                    height: 'var(--chip-h)',
                    boxSizing: 'border-box',
                    font: '600 var(--fs-note)/16px var(--font-ui)',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <Icon name="link" size={11} />
                  {l.text}
                </span>
              ))}
            </div>
          )}
        </div>
        {/* The one Buy: its count comes from the header's switch. A fixed width (item 273). */}
        <span data-testid={`buy-${row.id}`} style={{ display: 'grid', minWidth: 0 }}>
          <TwoLine
            look="primary"
            tall
            action={`Buy ${offer.n}`}
            line={short !== null ? `Needs ${short}` : coins(offer.cost)}
            off={short !== null}
            reason={short !== null ? `Needs ${short}` : undefined}
            label={
              short !== null ? `${what}, needs ${short} more` : `${what} for ${coins(offer.cost)}`
            }
            width={BUY_W}
            onClick={(e) => onBuy(row, offer.n, e.currentTarget as Element)}
          />
        </span>
      </div>
      {row.hire && <HireLine row={row} onHire={onHire} />}
    </div>
  );
}

/**
 * D454: the Hire line, the row's second line while it has no hand: a dashed rule above, a 6%
 * accent wash, the hand's glyph, what a hand does and a 96px Hire with its price. One the coins
 * do not cover takes the disabled look and says what it needs. It goes once the hand is hired.
 */
function HireLine({ row, onHire }: Pick<ProducerRowProps, 'row' | 'onHire'>) {
  const hire = row.hire;
  if (!hire) return null;
  const short = hire.needs !== undefined ? `Needs ${coins(hire.needs)}` : undefined;
  const hand = term('hand');
  return (
    <div
      data-testid={`hire-line-${row.id}`}
      style={{
        display: 'grid',
        gridTemplateColumns: `20px minmax(0, 1fr) ${BUY_W}px`,
        columnGap: 10,
        alignItems: 'center',
        padding: 10,
        borderTop: '1px dashed var(--line)',
        margin: '0 -10px -10px',
        borderRadius: '0 0 var(--radius-lg) var(--radius-lg)',
        background: 'color-mix(in srgb, var(--accent) 6%, var(--surface))',
      }}
    >
      <span style={{ color: 'var(--accent)', display: 'grid', placeItems: 'center' }}>
        <HandGlyph size={20} />
      </span>
      <span style={{ font: '400 12px/1.35 var(--font-ui)', minWidth: 0 }}>
        <strong style={{ fontWeight: 600 }}>Hire {aTerm(hand)}</strong> and it runs on its own, even
        while you&rsquo;re away
      </span>
      <TwoLine
        look="accent"
        action="Hire"
        line={short ?? coins(hire.cost)}
        off={short !== undefined}
        reason={short}
        testId={`hire-${row.id}`}
        label={`Hire ${aTerm(hand)} for ${row.name.toLowerCase()}, ${coins(hire.cost)}`}
        width={BUY_W}
        onClick={(e) => onHire(row, e.currentTarget as Element)}
      />
    </div>
  );
}

export interface LockedRowProps {
  row: LockedRow;
  onUnlock: (row: LockedRow, el: Element) => void;
}

/** The next goal: a producer not yet unlocked, what it earns and what it costs. */
export function LockedRowView({ row, onUnlock }: LockedRowProps) {
  if (row.opens)
    // D251: a producer behind festivals is a quiet goal: what it earns and when it opens.
    return (
      <div
        data-testid={`locked-${row.id}`}
        style={{
          ...ROW,
          gridTemplateColumns: '64px minmax(0, 1fr)',
          gridTemplateRows: 'auto',
          background: 'transparent',
          borderStyle: 'dashed',
          color: 'var(--ink-2)',
        }}
      >
        <div style={{ display: 'grid', placeItems: 'center', opacity: 0.6 }}>
          <Icon name={row.icon} size={26} />
        </div>
        <div style={{ display: 'grid', gap: 3, minWidth: 0 }}>
          <span style={{ font: '600 14px/1.2 var(--font-ui)' }}>{row.name}</span>
          <span style={{ ...SMALL, whiteSpace: 'normal' }}>
            <Figs text={row.opens} />
          </span>
        </div>
      </div>
    );
  const reason = row.needs !== undefined ? `Needs ${coins(row.needs)}` : undefined;
  return (
    <div
      data-testid={`locked-${row.id}`}
      style={{
        ...ROW,
        gridTemplateRows: 'auto',
        background: 'var(--surface-2)',
        borderStyle: 'dashed',
      }}
    >
      <div
        style={{
          width: 64,
          height: 52,
          display: 'grid',
          placeItems: 'center',
          color: 'var(--ink-2)',
          opacity: 0.7,
        }}
      >
        <Icon name={row.icon} size={30} />
      </div>
      <div style={{ display: 'grid', gap: 3, minWidth: 0 }}>
        <span style={{ font: '600 var(--card-title)/1.2 var(--font-ui)' }}>{row.name}</span>
        <span style={{ ...SMALL, whiteSpace: 'normal' }}>
          Earns <span class="tnum">{rateText(row.rate)}</span>
        </span>
      </div>
      <BuyButton
        needs={reason}
        onClick={(e) => onUnlock(row, e.currentTarget as Element)}
        label={`Unlock ${row.name.toLowerCase()}, ${coins(row.unlockCost)}`}
      >
        <PriceLabel verb="Unlock" cost={row.unlockCost} />
      </BuyButton>
    </div>
  );
}

export interface ChainRowProps {
  row: ChainRow;
  onBuild: (row: ChainRow, el: Element) => void;
}

/**
 * D439, D442: a chain's build card straight under its producer, indented 18px with a 2px line
 * connector and a dashed border: the producer's tile, "Build a mill", what it does, and a 96px
 * primary Build with its price. Locked, the tile shows a lock, the line says when it opens and
 * the button is disabled with "Locked" on its second line. Once built the card goes (the chip
 * shows on the producer's note line).
 */
export function ChainRowView({ row, onBuild }: ChainRowProps) {
  if (row.built) return null;
  const locked = row.locked !== undefined;
  const short = row.needs !== undefined ? `Needs ${coins(row.needs)}` : undefined;
  const off = locked || short !== undefined;
  const line = locked ? 'Locked' : (short ?? coins(row.cost));
  return (
    <div
      data-testid={`chain-${row.id}`}
      style={{
        position: 'relative',
        marginLeft: 18,
        marginTop: -2,
      }}
    >
      {/* The connector: a 2px line down the indent from the producer's card, then across. */}
      <span
        aria-hidden="true"
        data-connector=""
        style={{
          position: 'absolute',
          left: -10,
          top: -4,
          height: 'calc(50% + 4px)',
          width: 10,
          boxSizing: 'border-box',
          borderLeft: '2px solid var(--ink-2)',
          borderBottom: '2px solid var(--ink-2)',
          borderBottomLeftRadius: 6,
        }}
      />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          boxSizing: 'border-box',
          padding: 10,
          borderRadius: 'var(--radius-lg)',
          background: 'var(--surface)',
          border: '1px dashed var(--ink-2)',
        }}
      >
        <span aria-hidden="true" style={{ ...TILE, color: locked ? 'var(--ink-2)' : 'var(--ink)' }}>
          {locked ? <Icon name="lock" size={20} /> : <Icon name={row.icon} size={22} />}
        </span>
        <span style={{ flex: 1, minWidth: 0, display: 'grid', gap: 2 }}>
          <span style={{ font: '600 14px/1.3 var(--font-ui)' }}>{row.title}</span>
          <span style={{ font: '400 12px/1.3 var(--font-ui)', color: 'var(--ink-2)' }}>
            <Figs text={locked ? (row.locked ?? '') : row.effect} />
          </span>
        </span>
        <TwoLine
          look="primary"
          action="Build"
          line={line}
          off={off}
          reason={locked ? row.locked : short}
          width={96}
          label={
            locked
              ? `${row.title}, locked. ${row.locked}`
              : short !== undefined
                ? `${row.title}, ${short.toLowerCase()} more`
                : `${row.title} for ${coins(row.cost)}`
          }
          onClick={(e) => onBuild(row, e.currentTarget as Element)}
        />
      </div>
    </div>
  );
}
